import { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useStatusMessage, useTranslation } from "Shared/util/custom-hooks";
import {
  getSessionCookie,
  setSessionCookieHasExpired,
  hasValidSessionCookie,
} from "Shared/util/user-session";
import { getLink, getTitle, getReturn } from "Shared/navigation/login/routes";
import ReturnTitle from "Shared/components/return-title";
import StatusMessage from "Shared/components/status-message";
import Sidebar from "Shared/components/sidebar";
import { UserProvider } from "Contexts/user-context";
import LoginForm from "./forms/login-form";
import ActivateAccountForm from "./forms/activate-account-form";
import ChangePasswordForm from "./forms/change-password-form";
import ForgotPasswordForm from "./forms/forgot-password-form";
import UpdateAccountForm from "./forms/update-account-form";
import styles from "./login.css";
import useAppContext from "Contexts/app-context";
import { ImboxToggler } from "./ImboxToggler";

const cloneObject = (obj) => JSON.parse(JSON.stringify(obj));

const Login = ({ history, location }) => {
  const [statusMessage, dispatchStatusMessage] = useStatusMessage();
  const {
    appData: { brandName },
  } = useAppContext();
  const t = useTranslation((x) => x.login);
  const sidebarRef = useRef();
  const shouldLoadImbox = brandName === "EuroFinans";

  const scrollSidebarTop = () => {
    if (sidebarRef && sidebarRef.current) {
      sidebarRef.current.scrollTop = 0;
    }
  };

  useEffect(() => {
    if (statusMessage.type !== "NONE") {
      scrollSidebarTop();
    }
  }, [statusMessage]);

  const popLocationState = (key) => {
    if (!location || !location.state) {
      return null;
    }
    const { state } = location;
    const oldState = cloneObject(state);
    delete state[key];
    history.replace({ ...location, state });
    return oldState[key];
  };

  const getLocationPath = () => {
    if (!location) {
      return null;
    }
    return location.pathname;
  };
  const locationPath = getLocationPath();

  const routeEquals = (string) => locationPath === getLink(string);

  const handleRedirectMessage = () => {
    const redirectMessage = popLocationState("message");
    if (redirectMessage) {
      dispatchStatusMessage({
        type: redirectMessage.type,
        message: redirectMessage.message,
      });
    } else {
      dispatchStatusMessage({ type: "NONE" });
    }
  };

  const clearSessionCookie = () => {
    if (getSessionCookie()) {
      if (!hasValidSessionCookie()) {
        dispatchStatusMessage({
          type: "FAILED",
          message: t.youHaveBeenLoggedOutBecauseYouHaveNotBeenActive,
        });
      }
      setSessionCookieHasExpired();
    }
  };

  useEffect(() => {
    handleRedirectMessage();
    clearSessionCookie();
  }, [location.pathname]);

  const pageTitle = t[getTitle(locationPath)];
  const returnLink = getReturn(locationPath);

  return (
    <UserProvider alwaysRender>
      <div className={`theme__login__background ${styles.loginContainer}`}>
        {locationPath && (
          <Sidebar
            ref={sidebarRef}
            show
            animation={false}
            fullHeight
            overlay={false}
            className={styles.loginPanel}
          >
            <StatusMessage
              status={statusMessage.type}
              text={statusMessage.message}
              clearStatus={() => dispatchStatusMessage({ type: "NONE" })}
              className={styles.statusMessageContainer}
            />
            {returnLink ? (
              <ReturnTitle
                title={t[returnLink.title]}
                onClick={() => history.push(returnLink.link)}
                display={!!returnLink}
                className={styles.returnHeader}
              />
            ) : (
              <h2 className={styles.defaultHeader}>{pageTitle}</h2>
            )}
            <div className={styles.contentContainer}>
              {routeEquals("activate-account") && (
                <ActivateAccountForm
                  dispatchStatusMessage={dispatchStatusMessage}
                />
              )}
              {routeEquals("login") && (
                <LoginForm dispatchStatusMessage={dispatchStatusMessage} />
              )}
              {routeEquals("forgot-password") && (
                <ForgotPasswordForm
                  dispatchStatusMessage={dispatchStatusMessage}
                />
              )}
              {routeEquals("change-password") && (
                <ChangePasswordForm
                  dispatchStatusMessage={dispatchStatusMessage}
                />
              )}
              {routeEquals("update-account") && (
                <UpdateAccountForm
                  dispatchStatusMessage={dispatchStatusMessage}
                />
              )}
            </div>
            <div className={`${styles.bottomLogo} theme__login__logo`} />
          </Sidebar>
        )}
        {shouldLoadImbox && <ImboxToggler />}
      </div>
    </UserProvider>
  );
};

Login.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.object.isRequired,
};

export default Login;
